import React, { useState, useEffect } from 'react'
import { useForm, usePage, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Sparkles } from 'lucide-react'

type Props = {
  errors?: Record<string, string[]>
  canCreatePrivateRepo: boolean
  plan: string
  showUpgradeDialog?: boolean
}

export default function RepositoriesNew({
  errors = {},
  canCreatePrivateRepo,
  plan,
  showUpgradeDialog = false,
}: Props) {
  const { currentUser } = usePage().props as { currentUser: { username: string } }
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(showUpgradeDialog)

  const { data, setData, post, processing } = useForm({
    name: '',
    visibility: 'public_visibility',
  })

  useEffect(() => {
    setUpgradeDialogOpen(showUpgradeDialog)
  }, [showUpgradeDialog])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/repositories')
  }

  const handleVisibilityChange = (value: string) => {
    if (value === 'private_visibility' && !canCreatePrivateRepo) {
      setUpgradeDialogOpen(true)
      return
    }
    setData('visibility', value)
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>新しいリポジトリを作成</CardTitle>
            <CardDescription>
              コンテナイメージを保存するリポジトリを作成します
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {errors.base && (
                <Alert variant="destructive">
                  <AlertDescription>{errors.base[0]}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">リポジトリ名</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{currentUser.username} /</span>
                  <Input
                    id="name"
                    type="text"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                    placeholder="my-app"
                    className="flex-1"
                    required
                  />
                </div>
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  英数字、ドット、アンダースコア、ハイフンが使用できます
                </p>
              </div>

              <div className="space-y-3">
                <Label>公開設定</Label>
                <RadioGroup
                  value={data.visibility}
                  onValueChange={handleVisibilityChange}
                >
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="public_visibility" id="public" />
                    <div>
                      <Label htmlFor="public" className="font-medium">
                        Public
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        誰でも pull できます
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem
                      value="private_visibility"
                      id="private"
                      disabled={!canCreatePrivateRepo && plan === 'Free'}
                    />
                    <div>
                      <Label htmlFor="private" className="font-medium">
                        Private
                        {!canCreatePrivateRepo && plan === 'Free' && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (上限に達しています)
                          </span>
                        )}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        あなただけがアクセスできます
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" disabled={processing}>
                リポジトリを作成
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>

      {/* アップグレード促進ダイアログ */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              Pro プランにアップグレード
            </DialogTitle>
            <DialogDescription>
              Free プランではプライベートリポジトリは 1 つまでです。
              Pro プランにアップグレードすると、無制限のプライベートリポジトリを作成できます。
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">¥500</span>
                <span className="text-muted-foreground">/月（税別）</span>
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                <li>✓ 無制限のプライベートリポジトリ</li>
                <li>✓ 50GB ストレージ</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
              後で
            </Button>
            <Link href="/settings/billing">
              <Button>アップグレード</Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
